import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2, Loader2, Phone, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const inputCls =
  "w-full rounded-xl border border-navy/15 bg-white px-4 py-3 text-sm text-navy outline-none transition-colors duration-300 focus:border-cognac placeholder:text-stone-400";

const initialForm = { name: "", phone: "", postal_code: "", reason: "" };

export const CallbackModal = ({ open, onClose }) => {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canSubmit = form.name.trim().length >= 2 && form.phone.trim().length >= 8;

  const handleClose = () => {
    onClose();
    setForm(initialForm);
    setSent(false);
  };

  const submit = async () => {
    setSending(true);
    try {
      await api.post("/callback-request", form);
      setSent(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="callback-modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[120] flex items-center justify-center bg-navy/60 p-5 backdrop-blur-sm"
          onClick={handleClose}
        >
          <motion.div
            data-testid="callback-modal"
            initial={{ opacity: 0, y: 20, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.97 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md rounded-3xl border border-navy/10 bg-white p-7 shadow-2xl sm:p-9"
          >
            <button
              data-testid="callback-modal-close"
              onClick={handleClose}
              aria-label="Fermer"
              className="absolute right-5 top-5 rounded-full p-1.5 text-stone-400 transition-colors duration-300 hover:bg-cream hover:text-navy"
            >
              <X className="h-4 w-4" />
            </button>

            {sent ? (
              <div data-testid="callback-success" className="flex flex-col items-center py-6 text-center">
                <CheckCircle2 className="h-12 w-12 text-sage" />
                <h3 className="mt-5 font-display text-2xl font-medium text-navy">Merci {form.name.split(" ")[0]} !</h3>
                <p className="mt-3 text-sm leading-relaxed text-stone-500">
                  Votre demande est bien reçue. Un conseiller CozyHome vous rappelle au {form.phone}.
                </p>
                <button
                  data-testid="callback-modal-done"
                  onClick={handleClose}
                  className="mt-7 rounded-full bg-navy px-7 py-3 text-sm font-bold text-white transition-colors duration-300 hover:bg-cognac"
                >
                  Fermer
                </button>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-cognac/10">
                    <Phone className="h-4.5 w-4.5 text-cognac" />
                  </span>
                  <div>
                    <h3 className="font-display text-xl font-medium text-navy">Être rappelé</h3>
                    <p className="text-xs text-stone-500">Un conseiller CozyHome vous recontacte rapidement.</p>
                  </div>
                </div>

                <div className="mt-6 space-y-4">
                  <input
                    data-testid="callback-name-input"
                    className={inputCls}
                    placeholder="Nom et prénom *"
                    value={form.name}
                    onChange={(e) => set("name", e.target.value)}
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      data-testid="callback-phone-input"
                      type="tel"
                      className={inputCls}
                      placeholder="Téléphone *"
                      value={form.phone}
                      onChange={(e) => set("phone", e.target.value)}
                    />
                    <input
                      data-testid="callback-postal-input"
                      className={inputCls}
                      placeholder="Code postal"
                      maxLength={5}
                      value={form.postal_code}
                      onChange={(e) => set("postal_code", e.target.value.replace(/\D/g, ""))}
                    />
                  </div>
                  <textarea
                    data-testid="callback-reason-input"
                    rows={3}
                    className={`${inputCls} resize-none`}
                    placeholder="Motif de votre demande (facultatif)"
                    value={form.reason}
                    onChange={(e) => set("reason", e.target.value)}
                  />
                </div>

                <button
                  data-testid="callback-submit-button"
                  disabled={!canSubmit || sending}
                  onClick={submit}
                  className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-cognac px-7 py-3.5 text-sm font-bold text-white transition-colors duration-300 hover:bg-navy disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {sending && <Loader2 className="h-4 w-4 animate-spin" />}
                  Envoyer ma demande
                </button>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
